/**
 * Achievements: what they are, and how far along each one is. Defined here in
 * code — never in the database, where anyone with the SQL console could edit
 * what one takes. The database only remembers which were earned, and when
 * (`achievements`, migration 2), so one is never taken back.
 *
 * Every one of them rewards keeping money, never moving it: spending less,
 * earning more, finishing goals, keeping to plans, and keeping at it.
 *
 * **Nothing from before you started using OKANE counts** (`startedOn`, a
 * setting written the first time a ledger is opened — see `stores/achievements`).
 * A month counts only when all of it came after that day, and only once it is
 * over; a goal only when it was finished after it; a plan's occurrence only
 * when it fell due after it.
 *
 * Pure. Adding one is one entry in `ACHIEVEMENTS` plus its two catalogue keys,
 * `achievement.<id>.title` and `achievement.<id>.desc` — no words live here.
 * Each `measure(facts)` answers `{ have, earnedOn }`: how far along it is
 * (out of `need`), and the day it was earned, or null.
 *
 * The facts, as `stores/achievements.js` gathers them:
 *   today, startedOn   'YYYY-MM-DD'
 *   months             [{ month, income, spent }], every month in order, from
 *                      the one before `startedOn`'s to the last one that is
 *                      over, real money only (no savings), both positive
 *   spendDays          the days since `startedOn` anything was spent, sorted
 *   planMatches        per plan occurrence due since `startedOn` that came as
 *                      planned, the day the last of its money arrived
 *   goals              [{ createdOn, completedOn }], completedOn null until done
 */

import { addDays, diffDays, endOfMonth, startOfMonth } from './dates.js'

/** A month that counts: wholly after the start, and over. */
const countable = (facts, month) =>
  startOfMonth(month) >= facts.startedOn && endOfMonth(month) < facts.today

/**
 * `need` countable months in a row, each passing `test(month, before)`. Earned
 * the day after the month that completed the run; until then, `have` is the
 * run so far.
 */
function monthStreak(need, test) {
  return (facts) => {
    let run = 0
    for (let i = 1; i < facts.months.length; i++) {
      const month = facts.months[i]
      if (!countable(facts, month.month)) continue
      run = test(month, facts.months[i - 1]) ? run + 1 : 0
      if (run >= need) return { have: need, earnedOn: addDays(endOfMonth(month.month), 1) }
    }
    return { have: run, earnedOn: null }
  }
}

/** Using OKANE for `need` days. */
const daysIn = (need) => (facts) => {
  const days = Math.max(0, diffDays(facts.startedOn, facts.today))
  return days >= need
    ? { have: need, earnedOn: addDays(facts.startedOn, need) }
    : { have: days, earnedOn: null }
}

/** The `need`th of a list of days, sorted: earned on it. */
const nth = (need, days) =>
  days.length >= need ? { have: need, earnedOn: days[need - 1] } : { have: days.length, earnedOn: null }

/**
 * `need` days in a row without spending anything, between the start and
 * yesterday — today is not over, and a coffee this afternoon would undo it.
 */
const quietDays = (need) => (facts) => {
  const spent = new Set(facts.spendDays)
  const last = addDays(facts.today, -1)
  let run = 0
  let best = 0
  for (let day = facts.startedOn; day <= last; day = addDays(day, 1)) {
    run = spent.has(day) ? 0 : run + 1
    if (run >= need) return { have: need, earnedOn: addDays(day, 1) }
    best = run
  }
  return { have: best, earnedOn: null }
}

const spentLess = (month, before) => month.spent < before.spent
const earnedMore = (month, before) => month.income > before.income
const inTheBlack = (month) => month.income > month.spent

const goalsMade = (facts) =>
  facts.goals.map((g) => g.createdOn).filter((d) => d && d >= facts.startedOn).sort()
const goalsDone = (facts) =>
  facts.goals
    .map((g) => g.completedOn)
    .filter((d) => d && d >= facts.startedOn && d <= facts.today)
    .sort()

/**
 * In the order they are shown. `family` groups a ladder (one after the other
 * in the same colour); `icon` is an `Icon.vue` name; `unit` says what `need`
 * counts, for the progress line (`achievements.unit.<unit>`).
 */
export const ACHIEVEMENTS = [
  { id: 'saver', family: 'saver', icon: 'out', need: 1, unit: 'months', measure: monthStreak(1, spentLess) },
  { id: 'superSaver', family: 'saver', icon: 'out', need: 3, unit: 'months', measure: monthStreak(3, spentLess) },
  { id: 'ultraSaver', family: 'saver', icon: 'out', need: 6, unit: 'months', measure: monthStreak(6, spentLess) },
  { id: 'earner', family: 'earner', icon: 'in', need: 1, unit: 'months', measure: monthStreak(1, earnedMore) },
  { id: 'superEarner', family: 'earner', icon: 'in', need: 3, unit: 'months', measure: monthStreak(3, earnedMore) },
  { id: 'ultraEarner', family: 'earner', icon: 'in', need: 6, unit: 'months', measure: monthStreak(6, earnedMore) },
  { id: 'inTheBlack', family: 'black', icon: 'wallet', need: 1, unit: 'months', measure: monthStreak(1, inTheBlack) },
  { id: 'steadyHand', family: 'black', icon: 'wallet', need: 3, unit: 'months', measure: monthStreak(3, inTheBlack) },
  { id: 'quietWeek', family: 'quiet', icon: 'clock', need: 7, unit: 'days', measure: quietDays(7) },
  { id: 'perfectPlan', family: 'plan', icon: 'repeat', need: 50, unit: 'payments', measure: (f) => nth(50, f.planMatches) },
  { id: 'firstStep', family: 'goal', icon: 'target', need: 1, unit: 'goals', measure: (f) => nth(1, goalsMade(f)) },
  { id: 'goalOriented', family: 'goal', icon: 'target', need: 1, unit: 'goals', measure: (f) => nth(1, goalsDone(f)) },
  { id: 'overAchiever', family: 'goal', icon: 'target', need: 3, unit: 'goals', measure: (f) => nth(3, goalsDone(f)) },
  { id: 'accountant', family: 'time', icon: 'spark', need: 30, unit: 'days', measure: daysIn(30) },
  { id: 'ministerOfFinance', family: 'time', icon: 'spark', need: 100, unit: 'days', measure: daysIn(100) },
]

/** The catalogue keys the definitions are named by — for the test that proves they exist. */
export const achievementKeys = () =>
  ACHIEVEMENTS.flatMap((a) => [`achievement.${a.id}.title`, `achievement.${a.id}.desc`])

/**
 * Every achievement, measured. One already on record (`earned`: id → day) stays
 * earned on the day it was recorded, whatever the ledger says now — deleting a
 * transaction does not take a badge back.
 *
 * @returns {object[]} `{ ...definition, have, earned, earnedOn, recorded }`
 */
export function measureAchievements(facts, earned = new Map()) {
  return ACHIEVEMENTS.map((a) => {
    const recordedOn = earned.get(a.id) ?? null
    const { have, earnedOn } = facts ? a.measure(facts) : { have: 0, earnedOn: null }
    const on = recordedOn ?? earnedOn
    return {
      ...a,
      have: on ? a.need : Math.min(have, a.need),
      earned: !!on,
      earnedOn: on,
      recorded: !!recordedOn,
    }
  })
}
